import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from "react"
import { GetApp } from "../../App"
import { GetShip } from "../../App/Context"
import { Ship } from "../../App/Ship"
import { useEditState } from "../../App/useEditState"
import { AnchorMonitorAdvice } from "./AnchorMonitorAdvice"
import { AnchorWatchFailure, anchorWatchFailure } from "./AnchorWatchFailure"
import { keepAliveOptedIn } from "../../App/KeepAlive"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { EditGroup, EditGroupData, EditGroupFieldDefinitions } from "../../Components/EditGroup"
import { NumberInput } from "../../Components/Input"
import { ToggleSwitch } from "../../Components/ToggleSwitch"
import { useOverlayScroll } from "../../Components/useOverlayScroll"
import { OverlayScrollFooter } from "../../Components/OverlayScrollFooter"
import { OverlayViewDefinition } from "../OverlayView"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { LocalizationMessages } from "../../App/Localization"
import { logger } from "../../App/Logger"
import {
  IAlertThreshold,
  IAnchorDepthConfig,
  IAnchorFixedRadiusConfig,
  IAnchorMonitorData,
  IAnchorRodeScopeConfig,
  IAnchorSectorConfig,
  IAnchorWatchMethod,
  IAnchorWatchRequest,
} from "../../Types"
import { UserAdviceViewsData } from "./Context"
import { UserAdviceAnchorMonitorViewProps } from "./Params"

// The smallest drift band (meters) a stored configuration can plausibly mean, and
// the reading of one that is smaller. The bands were nautical miles before #1809,
// so a stored 0.1 is a leftover nm value, not a 10 cm swing circle: shown in a
// whole-meter field it reads as `0`, and saving it back would store a zero radius.
// Such a band is treated as unset so the field seeds from the default instead --
// the same rule the server applies in `AnchorDrift.sanitized`.
const minimumBandMeters = 1;

function sanitizeBand(meters: number | undefined): number | undefined {
  return meters != undefined && meters >= minimumBandMeters ? meters : undefined;
}

// The shipped `NavigationThresholds.anchorDrift` defaults (meters), mirrored here so
// the dialog can show what an unset band actually evaluates against. Reached only
// when the ship's own default is missing or is itself a leftover nautical-mile
// value; without it those fields would be blank, which is precisely the "I cannot
// tell what the defaults are" the metric switch produced.
const defaultBands: IAlertThreshold = { warning: 30, alarm: 50 };

// Fill a config's unset warning/alarm bands from a ship-wide `AlertThreshold`
// default (#1198). Every other field of the config (rode / boat length / sector
// bearings) is preserved untouched -- only the two bands have a ship-wide home.
function seedBands<T extends { warning?: number; alarm?: number }>(config: T | undefined, defaults: IAlertThreshold | undefined, sanitize = sanitizeBand, fallback?: IAlertThreshold): T {
  return {
    ...(config ?? {} as T),
    warning: sanitize(config?.warning) ?? sanitize(defaults?.warning) ?? fallback?.warning,
    alarm: sanitize(config?.alarm) ?? sanitize(defaults?.alarm) ?? fallback?.alarm,
  };
}

// Which request a submit builds. Save, Recenter and Enable are all submit buttons of
// the one form, so the button that was pressed decides what is sent alongside the
// edited configuration.
type AnchorWatchAction = "save" | "recenter" | "enable";

// The two drift bands, in meters (#1809) -- whole meters are precise enough for a
// swing circle, so every anchor distance is an `int` field.
const bandFields: EditGroupFieldDefinitions[] = [{ name: "warning", type: "int" }, { name: "alarm", type: "int" }];

// The form fields submitted for `method`. Only the active method's substruct is
// rendered, and therefore only it is submitted -- the server keeps the others.
// Anchor depth is independent of the method and always part of the form.
function editableFieldsFor(method: IAnchorWatchMethod): EditGroupFieldDefinitions[] {
  const methodFields: EditGroupFieldDefinitions = (() => {
    switch (method) {
      case IAnchorWatchMethod.rodeScope:
        return { name: "rodeScope", type: "object", fields: [{ name: "rode", type: "int" }, { name: "depth", type: "float" }, { name: "boatLength", type: "int" }, ...bandFields] };
      case IAnchorWatchMethod.sector:
        return { name: "sector", type: "object", fields: [{ name: "bearingFrom", type: "int" }, { name: "bearingTo", type: "int" }, ...bandFields] };
      default:
        return { name: "fixedRadius", type: "object", fields: bandFields };
    }
  })();
  return [methodFields, { name: "depth", type: "object", fields: [{ name: "warning", type: "float" }, { name: "alarm", type: "float" }] }];
}

// A submitted substruct as a config object: `EditGroup` yields `null` for an empty
// field, which has to become an absent key so the server keeps inheriting rather
// than storing a zero.
function submittedConfig<T>(values: EditGroupData, name: string): T {
  const submitted = (values[name] ?? {}) as Record<string, unknown>;
  const config: Record<string, number> = {};
  for (const [key, value] of Object.entries(submitted)) {
    if (typeof value == "number") {
      config[key] = value;
    }
  }
  return config as T;
}

// The anchor watch configurator + live control panel. Enable / disable the watch,
// pick the evaluation method (fixed radius / rode-scope / sector) and its
// parameters, recenter the swing circle onto the current fix, and drive acknowledge
// / snooze / clear on any open alert. Each method keeps its own parameter substruct
// in local state (seeded from the persisted config), so switching methods never
// discards the values entered for another — and because a save sends only the
// active method's substruct, the server likewise preserves the others. The
// swing-circle origin is seeded server-side, never sent from the device.
export function UserAdviceAnchorMonitorView({ advice, state, initFailed, initKeepAlive }: UserAdviceAnchorMonitorViewProps) {
  const app = GetApp();
  const t = app.getT("UserAdviceAnchorMonitorView");
  const ship = GetShip();
  const { bodyRef, showScrollButton, isAtBottom, handleScrollMore } = useOverlayScroll();
  const [edited, onEdited] = useEditState();

  const [failed, setFailed] = useState<AnchorWatchFailure | undefined>(
    initFailed === true ? AnchorWatchFailure.generic : (initFailed || undefined));

  const userAdvice = useMemo(() => advice.parent as AnchorMonitorAdvice, [advice]);
  const enabled = userAdvice.enabled;
  const config = useMemo<IAnchorMonitorData | undefined>(() => {
    if (!userAdvice.monitor?.data) {
      return undefined;
    }
    try {
      return JSON.parse(userAdvice.monitor.data) as IAnchorMonitorData;
    } catch (e) {
      logger.error("anchor monitor data parse", e);
      return undefined;
    }
  }, [userAdvice]);

  // Ship-wide drift / depth defaults (#1198). These thresholds were removed from the
  // NMEA Conditions tab, so this dialog is their only editing surface: a `.primary`
  // user's save is mirrored back into the ship's NavigationThresholds server-side, and
  // an unset per-watch band inherits from them. Seeding the fields from those defaults
  // surfaces the last used values instead of blank inputs over an invisible fallback.
  const thresholds = useMemo(() => Ship.shipSettings(ship).thresholds, [ship]);

  // One state bag per method so edits to one are retained while another is shown.
  const [method, setMethod] = useState<IAnchorWatchMethod>(config?.method ?? IAnchorWatchMethod.fixedRadius);
  const [fixedRadius, setFixedRadius] = useState<IAnchorFixedRadiusConfig>(seedBands(config?.fixedRadius, thresholds?.anchorDrift, sanitizeBand, defaultBands));
  const [rodeScope, setRodeScope] = useState<IAnchorRodeScopeConfig>(seedBands(config?.rodeScope, thresholds?.anchorDrift, sanitizeBand, defaultBands));
  const [sector, setSector] = useState<IAnchorSectorConfig>(seedBands(config?.sector, thresholds?.anchorDrift, sanitizeBand, defaultBands));

  // Depth-below-limit alerting for this watch (#990): on by default, with an
  // optional per-watch warning/alarm override (nil fields inherit the ship default).
  // Independent of `method`, so it is sent on every save regardless of which
  // swing-circle evaluation method is active.
  // Depth bands never changed unit, and a sub-meter depth limit is a legitimate
  // setting, so they seed unsanitised.
  const [depth, setDepth] = useState<IAnchorDepthConfig>(seedBands(config?.depth, thresholds?.anchorDepth ?? thresholds?.depth, (v) => v));

  // Per-device opt-in to keep this companion alerting in the background while the
  // watch runs. Primary devices already self-keep (NMEA GPS), so the toggle is hidden
  // there. Toggling applies immediately via the advice.
  const [keepAlive, setKeepAlive] = useState<boolean>(initKeepAlive ?? keepAliveOptedIn());
  const toggleKeepAlive = useCallback(() => {
    const next = !keepAlive;
    setKeepAlive(next);
    userAdvice.setKeepAlive(app, next);
  }, [keepAlive, userAdvice, app]);

  // The active method's substruct is the only config sent; the server keeps the
  // rest, so no method's settings are lost when switching or saving. Anchor depth
  // is independent of the method and always sent.
  const configBody = useCallback((values: EditGroupData): IAnchorWatchRequest => {
    const methodBody = (() => {
      switch (method) {
        case IAnchorWatchMethod.rodeScope: return { rodeScope: submittedConfig<IAnchorRodeScopeConfig>(values, "rodeScope") };
        case IAnchorWatchMethod.sector: return { sector: submittedConfig<IAnchorSectorConfig>(values, "sector") };
        default: return { fixedRadius: submittedConfig<IAnchorFixedRadiusConfig>(values, "fixedRadius") };
      }
    })();
    return { method, depth: submittedConfig<IAnchorDepthConfig>(values, "depth"), ...methodBody };
  }, [method]);

  // Only dismiss the dialog once the update actually succeeds — e.g. a user
  // without sufficient permission gets a 401, which must stay visible instead
  // of being swallowed behind an optimistically-closed dialog (#1146).
  const submit = useCallback(async (body: IAnchorWatchRequest) => {
    setFailed(undefined);
    try {
      await UserAdviceViewsData.anchorWatch(app.apiClient, body);
      state.setSelected(undefined);
    } catch (e) {
      logger.error("anchor watch update", e);
      setFailed(anchorWatchFailure(e));
    }
  }, [app, state]);

  // Which button submitted the form. Save / Recenter / Enable all submit the same
  // edited configuration and differ only in what accompanies it, so the pressed
  // button is recorded here and read back in `onSave`.
  const action = useRef<AnchorWatchAction>("save");
  const onSave = useCallback(async (_data: FormData, values: EditGroupData) => {
    const body = configBody(values);
    switch (action.current) {
      case "recenter": return submit({ recenter: true, ...body });
      case "enable": return submit({ enabled: true, ...body });
      default: return submit(body);
    }
  }, [configBody, submit]);
  const submitting = useCallback((next: AnchorWatchAction) => () => { action.current = next; }, []);

  // A number input bound to `key` on an immutable config bag. The bag keeps the
  // typed value while another method is shown (the form only carries the fields it
  // renders), and seeds the field when this method comes back.
  function numberField<T>(
    label: string, name: string, obj: T, key: keyof T & string, setObj: Dispatch<SetStateAction<T>>,
    options?: { format?: "int" | "float"; unit?: string },
  ) {
    const value = obj[key] as number | undefined;
    const id = `anchor-monitor-${name}`;
    return (
      <BlockField label={label} labelFor={id}>
        <NumberInput
          id={id}
          name={name}
          format={options?.format ?? "int"}
          unit={options?.unit ?? "m"}
          className="block_field_small_12 center"
          defaultValue={value}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            // Functional update: two fields of the same bag can be edited before a
            // re-render, and a spread of the captured `obj` would drop the first edit.
            setObj((prev) => ({ ...prev, [key]: isNaN(parsed) ? undefined : parsed }) as T);
            onEdited();
          }}
        />
      </BlockField>
    );
  }

  // Method-specific explanation shown right below the selector, swapping content
  // as the user changes method — a first draft of the copy, to be refined once
  // @almute reviews the wording (round 2).
  function explanation(m: LocalizationMessages) {
    switch (method) {
      case IAnchorWatchMethod.rodeScope: return m.anchorMonitor.explanation.rodeScope;
      case IAnchorWatchMethod.sector: return m.anchorMonitor.explanation.sector;
      default: return m.anchorMonitor.explanation.fixedRadius;
    }
  }

  return (
    <>
      <div ref={bodyRef} className="Overlay-body">
        <Block className="UserAdviceDialog UserAdviceAnchorMonitorView">
          <BlockRow className="block-row-top20">
            <BlockField>
              <span className="bold">{t("method")}:{"\u00A0"}{"\u00A0"}</span>
              <select value={method} onChange={(e) => setMethod(e.target.value as IAnchorWatchMethod)}>
                <option value={IAnchorWatchMethod.fixedRadius}>{t("method_fixedRadius")}</option>
                <option value={IAnchorWatchMethod.rodeScope}>{t("method_rodeScope")}</option>
                <option value={IAnchorWatchMethod.sector}>{t("method_sector")}</option>
              </select>
            </BlockField>
          </BlockRow>
          <BlockRow>
            <LocalizedMessage className="UserAdviceAnchorMonitorView-explanation text-bigger" message={explanation} />
          </BlockRow>
          <EditGroup
            changed={edited}
            editableFields={editableFieldsFor(method)}
            manualSave={true}
            onSave={onSave}
          >
            {method == IAnchorWatchMethod.fixedRadius && (
              <BlockRow className="block-row-gap">
                {numberField(t("warning"), "fixedRadius.warning", fixedRadius, "warning", setFixedRadius)}
                {numberField(t("alarm"), "fixedRadius.alarm", fixedRadius, "alarm", setFixedRadius)}
              </BlockRow>
            )}
            {method == IAnchorWatchMethod.rodeScope && (
              <>
                <BlockRow>
                  {numberField(t("rode"), "rodeScope.rode", rodeScope, "rode", setRodeScope)}
                  {numberField(t("depth"), "rodeScope.depth", rodeScope, "depth", setRodeScope, { format: "float" })}
                  {numberField(t("boatLength"), "rodeScope.boatLength", rodeScope, "boatLength", setRodeScope)}
                </BlockRow>
                <BlockRow className="block-row-gap">
                  {numberField(t("warning"), "rodeScope.warning", rodeScope, "warning", setRodeScope)}
                  {numberField(t("alarm"), "rodeScope.alarm", rodeScope, "alarm", setRodeScope)}
                </BlockRow>
              </>
            )}
            {method == IAnchorWatchMethod.sector && (
              <>
                <BlockRow>
                  {numberField(t("bearingFrom"), "sector.bearingFrom", sector, "bearingFrom", setSector, { unit: "\u00B0" })}
                  {numberField(t("bearingTo"), "sector.bearingTo", sector, "bearingTo", setSector, { unit: "\u00B0" })}
                </BlockRow>
                <BlockRow className="block-row-gap">
                  {numberField(t("warning"), "sector.warning", sector, "warning", setSector)}
                  {numberField(t("alarm"), "sector.alarm", sector, "alarm", setSector)}
                </BlockRow>
              </>
            )}
            <hr></hr>
            <BlockRow className="block-row-top20">
              {numberField(t("depthWarning"), "depth.warning", depth, "warning", setDepth, { format: "float" })}
              {numberField(t("depthAlarm"), "depth.alarm", depth, "alarm", setDepth, { format: "float" })}
            </BlockRow>
            {!app.primarySession() && (
              <BlockRow>
                <BlockField>
                  <ToggleSwitch enabled={keepAlive} toggleAction={toggleKeepAlive} />
                  <span>{t("keepAlive")}</span>
                </BlockField>
              </BlockRow>
            )}
            <hr className="block-row-top20" />

            {failed && (
              <BlockRow>
                <BlockField>
                  <span className="UserAdviceAnchorMonitorView-error text-error">
                    {t(failed === AnchorWatchFailure.generic ? "error" : `error_${failed}`)}
                  </span>
                </BlockField>
              </BlockRow>
            )}

            <BlockRow actions={true} className="block-row-gap">
              <BlockField><button className="button" type="button" onClick={() => state.setSelected(undefined)}>{t("cancel")}</button></BlockField>
              {enabled && <BlockField><button className="button" type="submit" onClick={submitting("recenter")}>{t("recenter")}</button></BlockField>}
              <BlockField><button className="button" type="submit" onClick={submitting("save")}>{t("save")}</button></BlockField>
              {enabled
                ? <BlockField><button className="button default" type="button" onClick={() => submit({ enabled: false })}>{t("disable")}</button></BlockField>
                : <BlockField><button className="button default" type="submit" onClick={submitting("enable")}>{t("enable")}</button></BlockField>}
            </BlockRow>
          </EditGroup>
        </Block>
      </div>
      <OverlayScrollFooter isAtBottom={isAtBottom} showScrollButton={showScrollButton} handleScrollMore={handleScrollMore} readMoreLabel={t("read_more")} />
    </>
  );
}

// This dialog's own overlay attributes (#1171), declared here rather than in
// `UserAdviceOverlayView` so the dialog keeps deciding how it presents itself. It
// takes its title straight from the advice whose button opened it, and stays
// closable unless that advice asked otherwise. Deliberately hook-free -- one of
// these is called per open advice, so a hook would vary React's hook count.
export function userAdviceAnchorMonitorOverlay(props: UserAdviceAnchorMonitorViewProps): OverlayViewDefinition {
  return {
    close: !props.advice.props.hideClose,
    title: props.advice.props.title,
    className: "overlay-max-height",
    render: () => <UserAdviceAnchorMonitorView {...props} />,
  };
}
